import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import Skeleton from '@material-ui/lab/Skeleton';
import EditAdvertiserModal from './EditAdvertiserModal';
import usePost from '../../../Hooks/usePost';
import { StyledButton } from '../../../Components/Generics.styled';

const AdvertiserInfoContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const AdvertiserInfoApplet = styled.div`
    margin-top: 40px;
    border: 1px solid black;
    height: 100%;
    width: 100%;
    max-width: 800px;
    text-align: center;
    padding: 12px;
    background-color: #fafbf7;
    border-radius: 12px;
    opacity: 1;
`;

const Label = styled.div`
    padding: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000000;
    text-align: left;
`;

const StyledValue = styled.span`
    letter-spacing: 0px;
    color: #000000;
    padding-left: 8px;
    font-weight: bold;
`;

const StyledHR = styled.hr`
    margin-top: 0px;
    margin-bottom: 0px;
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    margin-top: 80px;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    text-align: center;
`;

const AdvertiserInformation = ({
    setShouldFetchAdvertiserData,
    setShouldFetchOtherData,
    currentAdvertiser,
    setCurrentAdvertiser,
    profile,
}) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { post: postUpdateAdvertiser } = usePost('/updateAdvertiser');
    const { post: postUpdateAdvertiserInAgency } = usePost('/updateAdvertiserInAgency');

    const role = {
        isAgency: currentAdvertiser?.isAgency,
        isAgencyManager: currentAdvertiser?.isAgencyManager,
        isAgencyPrimary: currentAdvertiser?.isAgencyPrimary,
        canIUseAgencyAdvertisers: profile?.canIUseAgencyAdvertisers,
    };

    const [postData, setPostData] = useState({
        success: false,
        error: false,
        isLoading: false,
    });

    const [advertiserInfo, setAdvertiserInfo] = useState({});

    const {
        companyName,
        companyWebsite,
        firstName,
        lastName,
        title,
        primaryPhone,
        mobilePhone,
        streetAddress,
        city,
        province,
        country,
        zipCode,
        planTypeName,
        planTypeId,
        advertiserId,
        isAgencyPrimary,
        isAgency,
        agencyId,
        isBilledAnnually,
        stripeCustomerId,
    } = { ...advertiserInfo };

    const noAccountData = Object.keys(advertiserInfo).length === 0;

    const resetFields = () => {
        setAdvertiserInfo({
            ...currentAdvertiser,
        });
    };

    useEffect(() => {
        resetFields();
    }, [currentAdvertiser]);

    const saveAdvertiserData = async () => {
        setPostData({ ...postData, isLoading: true });
        try {
            const body = {
                advertiserId,
                isAgencyPrimary,
                isAgency,
                agencyId,
                planTypeId,
                planTypeName,
                companyName,
                companyWebsite,
                firstName,
                lastName,
                title,
                primaryPhone,
                mobilePhone,
                streetAddress,
                country,
                city,
                province,
                zipCode,
                stripeCustomerId,
                isBilledAnnually,
            };
            if (role.isAgencyManager) {
                await postUpdateAdvertiserInAgency(body, 'POST');
                setCurrentAdvertiser(body);
                setShouldFetchAdvertiserData(true);
                setShouldFetchOtherData(true);
            } else {
                await postUpdateAdvertiser(body, 'POST');
                setCurrentAdvertiser(body);
            }

            setPostData({ ...postData, isLoading: false, success: true });
        } catch (err) {
            setPostData({ ...postData, isLoading: false, success: false, error: true });
        }
    };

    const { error } = { ...postData };

    return (
        <AdvertiserInfoContainer>
            <Title>Advertiser Information</Title>
            <AdvertiserInfoApplet>
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Company Name:
                        <StyledValue>{companyName}</StyledValue>{' '}
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Company Website:
                        <StyledValue>{companyWebsite}</StyledValue>
                    </Label>
                )}

                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        First Name:
                        <StyledValue>{firstName}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Last Name:
                        <StyledValue>{lastName}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Your Title:
                        <StyledValue>{title}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Primary Phone:
                        <StyledValue>{primaryPhone}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Mobile Phone:
                        <StyledValue>{mobilePhone}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Street:
                        <StyledValue>{streetAddress}</StyledValue>
                    </Label>
                )}

                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Country:
                        <StyledValue>{country}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        State:
                        <StyledValue>{province}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        City:
                        <StyledValue>{city}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Zip:
                        <StyledValue>{zipCode}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Plan Type:
                        <StyledValue>{planTypeName}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {noAccountData || error ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Account Type:
                        <StyledValue>Advertiser</StyledValue>
                    </Label>
                )}
                <StyledHR />
                <br />
                {role.isAgency &&
                (role.isAgencyPrimary || !role.canIUseAgencyAdvertisers) ? null : (
                    <StyledButton onClick={() => setModalIsOpen(true)}>Update</StyledButton>
                )}
                <EditAdvertiserModal
                    advertiserInfo={advertiserInfo}
                    setAdvertiserInfo={setAdvertiserInfo}
                    setModalIsOpen={setModalIsOpen}
                    postData={postData}
                    modalIsOpen={modalIsOpen}
                    resetFields={resetFields}
                    saveAdvertiserData={saveAdvertiserData}
                />
            </AdvertiserInfoApplet>
        </AdvertiserInfoContainer>
    );
};

AdvertiserInformation.propTypes = {
    advertisers: propTypes.shape({}),
    setShouldFetchAdvertiserData: propTypes.func,
    setShouldFetchOtherData: propTypes.func,
    currentAdvertiser: propTypes.shape({
        isAgencyPrimary: propTypes.bool,
        isAgencyManager: propTypes.bool,
        isAgency: propTypes.bool,
    }),
    setCurrentAdvertiser: propTypes.func,
    profile: propTypes.shape({
        canIManageAgency: propTypes.bool,
        canIUseAgencyAdvertisers: propTypes.bool,
    }),
};

export default AdvertiserInformation;
